import React from 'react';

import { SelectedHistoricalPricingData } from '~/models/HistoricalPricingDataResult';
import { formatCurrencyInCents } from '~/utils/currencyFormatter';
import { Box, Button, Flex, Heading, LoadingSkeleton, Placeholder, Text } from '~/vinovest-design-system/components';
import type { CMSVintageProduct } from '~/networking/api/cms/responses/CMSWineVintage';
import type { ProductPriceResponse } from '~/networking/api/managed/requests/pricing/getProductPrice';

interface ProductHeaderProps {
    historicalPricingData?: SelectedHistoricalPricingData;
    productPrice?: ProductPriceResponse;
    displayName?: string;
    ticker?: string;
    vintage: string | number | undefined;
    selectedProduct: CMSVintageProduct;
    handleBottleSizeClick: (size: CMSVintageProduct) => void;
    products?: Array<CMSVintageProduct>;
}

export default function ProductHeader({
    historicalPricingData,
    productPrice,
    displayName,
    ticker,
    vintage,
    selectedProduct,
    handleBottleSizeClick,
    products
}: ProductHeaderProps) {
    return (
        <div className={'flex flex-wrap mt-5 lg:mt-10 w-full items-start'}>
            <Flex classnames={'pb-2 flex-wrap flex-col md:w-1/2'}>
                <Text variant={'label'} as={'p'} classnames={'text-12 uppercase font-mono'}>
                    {ticker}&nbsp;
                </Text>
                <h1 className={'heading-3 name mb-5'}>
                    {vintage} {displayName}
                </h1>
            </Flex>
            <Flex classnames={'pb-2 flex-wrap flex-col w-3/4 md:w-[auto]'}>
                <Flex classnames={'flex-auto'}>
                    <Flex classnames={'flex-auto pb-2 items-end'}>
                        <Text variant={'label'} as={'p'} classnames={'text-12 uppercase font-mono'}>
                            Bottle size (ML)
                        </Text>
                    </Flex>
                </Flex>
                <Box classnames={'flex flex-wrap gap-[10px]'}>
                    {products?.length &&
                        products.map((product) => (
                            <Button
                                key={`${product.bottles}${product.ml}`}
                                variant={product.id === selectedProduct.id ? 'platinum' : 'gray'}
                                onClick={() => handleBottleSizeClick(product)}
                                padding={'py-2 px-4'}
                            >
                                {product.bottles}x{product.ml}
                            </Button>
                        ))}
                </Box>
            </Flex>
            <Flex classnames={'flex flex-col items-end flex-auto justify-end'}>
                <Flex>
                    <Text variant={'label'} as={'p'} classnames={'text-12 uppercase pb-2 font-mono'}>
                        Current price
                    </Text>
                </Flex>
                <Flex>
                    <Placeholder
                        renderCondition={!!(historicalPricingData || productPrice?.price)}
                        placeholder={<LoadingSkeleton height={'40px'} width={'150px'} />}
                    >
                        {productPrice?.price && (
                            <Heading level={'h6'} type={'h3'}>
                                {formatCurrencyInCents(productPrice.price.amount, false, productPrice.price.currency)}
                            </Heading>
                        )}
                        {historicalPricingData && (
                            <Heading level={'h6'} type={'h3'}>
                                {formatCurrencyInCents(
                                    historicalPricingData?.prices[historicalPricingData?.prices?.length - 1]?.price
                                )}
                            </Heading>
                        )}
                    </Placeholder>
                </Flex>
            </Flex>
        </div>
    );
}
